import { CustomizedButton, CustomizedFormErrorMessage, CustomizedModalBase } from 'components/common/index';
import classes from 'components/common/ProgramLibraryDropdownMenu/components/RenameProjectModal/RenameProjectModal.module.scss';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { Controller, useForm } from 'react-hook-form';
import TextSearchInput from 'components/common/TextSearchInput/textSearchInput';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useMutationRenameProject } from 'api/mutations/renameProject.mutation';

interface Props {
    showModal: boolean;
    onHideModal: () => void;
}

interface IProjectName {
    projectName: string;
}

export const RenameProjectModal = ({ showModal, onHideModal }: Props) => {
    const { project, update } = useProjectTreeStore();

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate, isLoading } = useMutationRenameProject();

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
    } = useForm<IProjectName>({
        values: {
            projectName: project ? project?.title : '',
        },
    });

    const handleSave = (value: IProjectName) => {
        if (!project) {
            return;
        }
        mutate(
            {
                projectName: value.projectName,
                projectId: project.id,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully renamed!', 'success');
                    update({ project: { ...project, title: value.projectName } });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            },
        );
    };

    const closeModal = () => {
        onHideModal();
        reset();
    };

    return (
        <CustomizedModalBase isModalOpen={showModal} handleModalClose={closeModal}>
            <form onSubmit={handleSubmit(handleSave)} autoComplete="off">
                <div className={classes.content}>
                    <div className={classes.title}>Project Rename</div>
                    <div className={classes.subTitle}>Current name: {project?.title}</div>
                    <div className={classes.inputWrapper}>
                        <label>New name</label>
                        <Controller
                            render={({ field }) => (
                                <TextSearchInput
                                    {...field}
                                    id={'projectName'}
                                    isSearch={false}
                                    className={classes.input}
                                    placeholder="Enter project name..."
                                    maxLength={30}
                                    handleClear={() => {
                                        setValue('projectName', '');
                                    }}
                                />
                            )}
                            name="projectName"
                            control={control}
                            rules={{
                                validate: {
                                    min: (value) => (value && value.length >= 2) || 'must be at least 2 characters',
                                },
                            }}
                        />
                        {errors.projectName && <CustomizedFormErrorMessage text={errors.projectName.message} />}
                    </div>

                    <div className={classes.footer}>
                        <div className={classes.container}>
                            <CustomizedButton size={'large'} color={'secondary'} onClick={closeModal}>
                                Cancel
                            </CustomizedButton>

                            <CustomizedButton
                                type={'submit'}
                                size={'large'}
                                color={'primary'}
                                isLoading={isLoading}
                                disabled={isLoading}
                            >
                                Rename
                            </CustomizedButton>
                        </div>
                    </div>
                </div>
            </form>
        </CustomizedModalBase>
    );
};
